import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export function useTeamMembers() {
  return useQuery({
    queryKey: ["team-members"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_account")
        .select(`
          *,
          user_role!inner(
            role_id,
            role:role_id(*)
          )
        `)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      // Exclude customer users (client_viewer only) — those show on the Customers page
      return (data ?? []).filter((u: any) =>
        !u.user_role?.every((ur: any) => ur.role?.name === "client_viewer")
      );
    },
  });
}

export function useInactiveTeamMembers() {
  return useQuery({
    queryKey: ["inactive-team-members"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_account")
        .select(`
          *,
          user_role!inner(
            role_id,
            role:role_id(*)
          )
        `)
        .eq("is_active", false)
        .order("created_at", { ascending: false });

      if (error) throw error;
      // Exclude customer users (client_viewer only) — those show on the Customers page
      return (data ?? []).filter((u: any) =>
        !u.user_role?.every((ur: any) => ur.role?.name === "client_viewer")
      );
    },
  });
}

export function useUpdateUserRoles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, roleIds }: { userId: string; roleIds: string[] }) => {
      // Remove existing roles
      await supabase.from("user_role").delete().eq("user_id", userId);

      // Add new roles
      const inserts = roleIds.map((roleId) => ({
        user_id: userId,
        role_id: roleId,
      }));

      const { error } = await supabase.from("user_role").insert(inserts);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      queryClient.invalidateQueries({ queryKey: ["inactive-team-members"] });
      toast({
        title: "Roles updated",
        description: "User roles have been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating roles",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from("user_account")
        .update({ is_active: false })
        .eq("id", userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      queryClient.invalidateQueries({ queryKey: ["inactive-team-members"] });
      toast({
        title: "User deactivated",
        description: "User has been deactivated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deactivating user",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useReactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from("user_account")
        .update({ is_active: true })
        .eq("id", userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      queryClient.invalidateQueries({ queryKey: ["inactive-team-members"] });
      toast({
        title: "User reactivated",
        description: "User has been reactivated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error reactivating user",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("role")
        .select("*")
        .order("name");

      if (error) throw error;
      return data;
    },
  });
}
